import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from 'designSystem/components/Tooltip'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'

const TruncatedTextContainer = styled.span<{
  $maxWidth?: string
  $fillWidth?: boolean
}>`
  display: block;
  max-width: ${({ $maxWidth }) => $maxWidth || '15rem'};
  ${({ $fillWidth }) => $fillWidth && 'width: 100%;'}
  overflow: hidden;
  white-space: nowrap;
`

// Hidden element used to measure text width
const MeasureSpan = styled.span`
  position: absolute;
  visibility: hidden;
  white-space: nowrap;
  pointer-events: none;
`

export interface MiddleTruncatedTextProps {
  /** The text to truncate */
  text: string
  /** Maximum number of characters to display when not using dynamic width (default: 30) */
  maxLength?: number
  /** Maximum width of the container (default: '15rem') */
  maxWidth?: string
  /** Additional className for styling */
  className?: string
  /** Font size for the tooltip (e.g. '0.875rem'). If not provided, uses default tooltip size */
  tooltipFontSize?: string
  /** Whether to fill the available width (sets width: 100%) and use dynamic truncation */
  fillWidth?: boolean
}

/**
 * Component that truncates text in the middle, showing the beginning and end
 * with "..." in between. Useful for displaying long filenames or paths where
 * both the start and end are important for identification.
 *
 * @example
 * ```tsx
 * <MiddleTruncatedText text="very-long-document-name-with-many-parts.pdf" />
 * // Displays: "very-long-docu...parts.pdf"
 * ```
 */
export const MiddleTruncatedText: React.FC<MiddleTruncatedTextProps> = ({
  text,
  maxLength = 30,
  maxWidth = '15rem',
  className,
  tooltipFontSize,
  fillWidth = false,
}) => {
  const containerRef = useRef<HTMLSpanElement>(null)
  const measureRef = useRef<HTMLSpanElement>(null)
  const [displayText, setDisplayText] = useState(text)
  const [isTruncated, setIsTruncated] = useState(false)

  const calculateTruncation = useCallback(() => {
    if (!fillWidth) {
      // Use character-based truncation
      if (text.length <= maxLength) {
        setDisplayText(text)
        setIsTruncated(false)
      } else {
        const startLength = Math.floor((maxLength - 3) / 2)
        const endLength = Math.ceil((maxLength - 3) / 2)
        const start = text.substring(0, startLength)
        const end = text.substring(text.length - endLength)
        setDisplayText(`${start}...${end}`)
        setIsTruncated(true)
      }
      return
    }

    // Dynamic width-based truncation
    const container = containerRef.current
    const measure = measureRef.current
    if (!container || !measure) return

    const containerWidth = container.clientWidth
    if (containerWidth === 0) return

    // Measure full text width
    measure.textContent = text
    const fullWidth = measure.offsetWidth

    if (fullWidth <= containerWidth) {
      setDisplayText(text)
      setIsTruncated(false)
      return
    }

    // Binary search for the right amount of characters
    const ellipsis = '...'
    measure.textContent = ellipsis
    const ellipsisWidth = measure.offsetWidth

    // Available width for actual text (minus ellipsis)
    const availableWidth = containerWidth - ellipsisWidth

    // Find how many characters we can fit
    let low = 0
    let high = text.length
    let bestLength = 0

    while (low <= high) {
      const mid = Math.floor((low + high) / 2)
      const startLen = Math.floor(mid / 2)
      const endLen = Math.ceil(mid / 2)
      const testText =
        text.substring(0, startLen) + text.substring(text.length - endLen)

      measure.textContent = testText
      const testWidth = measure.offsetWidth

      if (testWidth <= availableWidth) {
        bestLength = mid
        low = mid + 1
      } else {
        high = mid - 1
      }
    }

    if (bestLength < 2) {
      // Not enough space, show minimal
      setDisplayText(ellipsis)
    } else {
      const startLen = Math.floor(bestLength / 2)
      const endLen = Math.ceil(bestLength / 2)
      const start = text.substring(0, startLen)
      const end = text.substring(text.length - endLen)
      setDisplayText(`${start}${ellipsis}${end}`)
    }
    setIsTruncated(true)
  }, [text, maxLength, fillWidth])

  useEffect(() => {
    calculateTruncation()

    if (!fillWidth) return

    const container = containerRef.current
    if (!container) return

    const resizeObserver = new ResizeObserver(() => {
      calculateTruncation()
    })

    resizeObserver.observe(container)
    return () => resizeObserver.disconnect()
  }, [calculateTruncation, fillWidth])

  if (!isTruncated) {
    return (
      <TruncatedTextContainer
        ref={containerRef}
        className={className}
        $maxWidth={maxWidth}
        $fillWidth={fillWidth}>
        {displayText}
        {fillWidth && <MeasureSpan ref={measureRef} />}
      </TruncatedTextContainer>
    )
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <TruncatedTextContainer
          ref={containerRef}
          className={className}
          $maxWidth={maxWidth}
          $fillWidth={fillWidth}>
          {displayText}
          {fillWidth && <MeasureSpan ref={measureRef} />}
        </TruncatedTextContainer>
      </TooltipTrigger>
      <TooltipContent
        style={tooltipFontSize ? { fontSize: tooltipFontSize } : undefined}>
        {text}
      </TooltipContent>
    </Tooltip>
  )
}
